import { useEffect, useRef } from 'react';

/**
 * Split-flap style header clock — `HH : MM : SS : MMM`, 24-hour, local time.
 *
 * ── Why refs and rAF instead of React state ────────────────────────────────
 * Milliseconds change 1000×/second. Holding the time in state would re-render
 * this component — and anything rendering with it — at that rate, which is
 * exactly the application-wide churn to avoid. ONE `requestAnimationFrame` loop
 * writes straight into DOM nodes via refs, so React renders this once and never
 * again. The loop is cancelled on unmount.
 *
 * rAF ticks at the display refresh (~60 Hz), so the millisecond field STEPS
 * (…428 → …445) rather than showing every integer. Intentional: no UI can paint
 * 1000 distinct values per second, and browsers throttle rAF in background
 * tabs, which is the behaviour we want.
 *
 * ── Visual hierarchy ───────────────────────────────────────────────────────
 * HH/MM/SS are the loud part: larger, tabular, on raised tiles that flip on
 * change. Milliseconds are deliberately quieter — smaller, muted, no flip. A
 * flip on a field changing ~60×/s reads as a blur and burns paint budget.
 *
 * ── Reduced motion ─────────────────────────────────────────────────────────
 * The flip is applied through a class carrying Tailwind's `motion-reduce`
 * variant, matching the convention established by `AuthorizationRadar`: under
 * `prefers-reduced-motion` the transition is removed and digits simply swap.
 * The clock keeps working — only the animation stops.
 */

/** One two-digit split-flap tile. */
function Tile({ innerRef, label }: { innerRef: React.RefObject<HTMLSpanElement>; label: string }) {
  return (
    <span
      // Fixed min width + tabular figures: the header must not shift as digits
      // change. The tile is the "premium" bit — a raised, slightly inset face.
      className="inline-flex min-w-[2.1ch] justify-center rounded-md border border-border/80 bg-gradient-to-b from-muted/40 to-muted/80 px-1.5 py-1 text-foreground shadow-sm ring-1 ring-inset ring-background/40 tabular-nums"
      data-testid={`clock-${label}`}
    >
      <span
        ref={innerRef}
        className="block transition-transform duration-150 ease-out will-change-transform motion-reduce:transition-none"
      >
        00
      </span>
    </span>
  );
}

function Separator() {
  return <span className="px-[1px] text-muted-foreground/70">:</span>;
}

export function HeaderClock() {
  const hoursRef = useRef<HTMLSpanElement>(null);
  const minutesRef = useRef<HTMLSpanElement>(null);
  const secondsRef = useRef<HTMLSpanElement>(null);
  const msRef = useRef<HTMLSpanElement>(null);
  /** Accessible, human-readable time — updated at most once per second. */
  const srRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    let frame = 0;
    // Previous values, so a tile is written (and flipped) only when it changes.
    let prevH = '';
    let prevM = '';
    let prevS = '';

    const flip = (el: HTMLSpanElement | null, value: string) => {
      if (!el) return;
      el.textContent = value;
      el.style.transform = 'rotateX(-72deg)';
      requestAnimationFrame(() => {
        el.style.transform = 'rotateX(0deg)';
      });
    };

    const tick = () => {
      const now = new Date();
      const h = String(now.getHours()).padStart(2, '0');
      const m = String(now.getMinutes()).padStart(2, '0');
      const s = String(now.getSeconds()).padStart(2, '0');
      const ms = String(now.getMilliseconds()).padStart(3, '0');

      if (h !== prevH) {
        flip(hoursRef.current, h);
        prevH = h;
      }
      if (m !== prevM) {
        flip(minutesRef.current, m);
        prevM = m;
      }
      if (s !== prevS) {
        flip(secondsRef.current, s);
        prevS = s;
        if (srRef.current) srRef.current.textContent = `${h}:${m}:${s}`;
      }
      // Milliseconds every frame — no flip, see the header comment.
      if (msRef.current) msRef.current.textContent = ms;

      frame = requestAnimationFrame(tick);
    };

    tick();
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <div
      // Absolutely centred rather than flexed: the left workspace badge and the
      // right-hand controls have different widths, so a flex spacer would push
      // the clock off true centre. `pointer-events-none` guarantees it can never
      // intercept a click meant for those controls.
      //
      // Hidden below `md` so it cannot crowd the mobile toggle and user menu.
      className="pointer-events-none absolute left-1/2 hidden -translate-x-1/2 select-none items-center md:flex"
      data-testid="header-clock"
    >
      <div
        role="timer"
        aria-label="Current time"
        // The tiles are a decorative duplicate of the sr-only value below, so
        // assistive tech is not told about millisecond churn.
        aria-live="off"
        className="flex items-center gap-[3px] rounded-xl border border-border/70 bg-card/70 px-2.5 py-1 font-mono text-[15px] font-semibold leading-none shadow-sm backdrop-blur-sm"
        style={{ perspective: '160px' }}
      >
        <Tile innerRef={hoursRef} label="hours" />
        <Separator />
        <Tile innerRef={minutesRef} label="minutes" />
        <Separator />
        <Tile innerRef={secondsRef} label="seconds" />
        <Separator />
        <span
          ref={msRef}
          data-testid="clock-milliseconds"
          className="min-w-[3.2ch] pl-[1px] text-center text-[11px] font-medium text-muted-foreground tabular-nums"
        >
          000
        </span>
      </div>

      <span ref={srRef} className="sr-only">
        00:00:00
      </span>
    </div>
  );
}
